import { CoreButton, CorePaymentInput } from '@components'
import CorePreviewLine from '@components/CorePreviewLine'
import { toastSuccess } from '@utils'
import { usePayoutInitiateMutation, usePayoutPreviewMutation } from 'mmfintech-backend-api'
import type { PaymentOption, TransactionViewPortal } from 'mmfintech-backend-api/types'
import {
  checkAmountLimits,
  fixFloat,
  GlobalContext,
  OtpContext,
  tr,
  translateError,
  useFormValues
} from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import moment from 'moment'
import { useContext, useState } from 'react'
import { v4 as uuid } from 'uuid'
import { PayoutPreview } from './PayoutPreview'

import './styled/payoutModal.scss'

export const PayoutModal = ({ transaction }: { transaction: TransactionViewPortal }) => {
  const { setOtpOnSuccess } = useContext(OtpContext)
  const { modalHide } = useContext(GlobalContext)

  const { amount, id, checkoutDetails, currency, paymentMethod, reqTime } = transaction || {}
  const { customerName, customerEmail } = checkoutDetails || {}

  const paymentOption: PaymentOption = {
    status: 'AVAILABLE',
    minAmount: 0,
    maxAmount: amount
  }

  const [payoutInit, { error: payoutInitError, isLoading: payoutInitFetching }] = usePayoutInitiateMutation({
    fixedCacheKey: 'payout-init'
  })
  const [payoutPreview, { error: payoutPreviewError, isLoading: payoutPreviewFetching, data: payoutPreviewData }] =
    usePayoutPreviewMutation({
      fixedCacheKey: 'payout-preview'
    })

  const isFormDataValid = () => {
    const test = checkAmountLimits(fixFloat(formValues.getValue('amount')), paymentOption)
    if (!test.valid) {
      formValues.setError('amount', translateError(test), test)
      return false
    }

    return true
  }
  const [step, setStep] = useState(1)

  const formValues = useFormValues({
    amount: {
      required: true,
      validation: 'float',
      value: transaction?.amount.toString()
    }
  })

  const handlePreview = async (payload: any) => {
    try {
      await payoutPreview(payload).unwrap()
      setStep(2)
    } catch (_err) {}
  }

  const handlePayoutInit = async (payload: any) => {
    setOtpOnSuccess(() => () => {
      toastSuccess(tr('FRONTEND.TRANSACTIONS.PAYOUT_SUCCESS', 'Payout initiated'))
    })
    try {
      await payoutInit(payload).unwrap()
    } catch {}
  }

  const handleSubmit = () => {
    if (isFormDataValid()) {
      const payload = {
        payoutForeignTransactionId: uuid(),
        originalTransactionId: transaction.id,
        amount: formValues.getValue('amount')
      }

      switch (step) {
        case 1:
          void handlePreview(payload)
          break

        case 2:
          void handlePayoutInit(payload)
          break

        default:
          break
      }
    }
  }
  return (
    <div className='payout-modal-wrapper'>
      {step === 1 ? (
        <>
          <div className='info-container'>
            <CorePreviewLine
              className={'transaction-payout-single-content'}
              title={tr('FRONTEND.PENDING_APPROVALS.MODAL.TRANSACTION_ID', 'Transaction ID')}
              text={id?.toString()}
            />
            <CorePreviewLine
              className={'transaction-payout-single-content'}
              title={tr('FRONTEND.EXPENSES.LABEL.DATE', 'Date')}
              text={moment(reqTime).format('DD/MM/YYYY')}
            />
            <CorePreviewLine
              className={'transaction-payout-single-content'}
              title={tr('FRONTEND.SEND_MONEY.PAYMENT_METHOD', 'Payment method')}
              text={tr(`METADATA.PAYMENT_OPTIONS.${paymentMethod}`, paymentMethod)}
            />
            <CorePreviewLine
              className={'transaction-payout-single-content'}
              title={tr('FRONTEND.TRANSACTIONS.PAYOUT_MODAL.CUSTOMER_NAME', 'Customer name')}
              text={customerName}
            />
            <CorePreviewLine
              className={'transaction-payout-single-content'}
              title={tr('FRONTEND.TRANSACTIONS.PAYOUT_MODAL.CUSTOMER_EMAIL', 'Customer email')}
              text={customerEmail}
            />
          </div>

          <div className='amount-container'>
            <CorePaymentInput
              type='label'
              currencyCodeOverride={currency}
              error={formValues.getError('amount')}
              value={formValues.getValue('amount')}
              onChange={value => formValues.setValue('amount', value)}
              paymentOption={paymentOption}
              dataTest='payout-amount'
              label={tr('FRONTEND.DEPOSIT.AMOUNT_LABEL', 'Amount')}
            />
          </div>
        </>
      ) : null}

      {step === 2 ? (
        <PayoutPreview previewData={payoutPreviewData} checkoutDetails={checkoutDetails} trn={transaction} />
      ) : null}

      <ErrorDisplay error={[payoutInitError, payoutPreviewError]} />

      <div className='payout-bottom-actions'>
        <CoreButton
          size='large'
          variation='primary'
          fullWidth
          onClick={handleSubmit}
          text={
            step === 1
              ? tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')
              : tr('FRONTEND.TRANSACTIONS.PAYOUT_BUTTON', 'Payout')
          }
          disabled={formValues.getFloat('amount') <= 0}
          isLoading={payoutInitFetching || payoutPreviewFetching}
        />

        <CoreButton
          size='large'
          variation='tertiary'
          onClick={modalHide}
          text={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
        />
      </div>
    </div>
  )
}
